import { useEffect, useMemo, useState } from "react";
import { InlineMath } from "react-katex";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { constructInputWithSpecialExpressionList } from "../../../utilization/CatchtivityUtilization";
import { ITrueFalseActivityMaterialProps } from "../../../properties/ActivityProperties";
import { seededShuffleArray } from "../../../utilization/AppUtilization";
import i18n from "../../../language/i18n";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

// Design-prototype layout (design system V4): a grid table —
// statement | Doğru | Yanlış — with teal check boxes. Prefix: mc-tf2-.
//
// GRADED REVIEW uses a THIRD AXIS. The column (Doğru/Yanlış) states a property of
// the STATEMENT; "was I right?" is a property of the LEARNER. Encoding both in one
// cell forced a verdict glyph under a header that already said doğru/yanlış — a ✓
// under "YANLIŞ" in the first version, a ✗ under "DOĞRU" in the second. Both were
// reported as unreadable, because the collision is in the vocabulary, not the glyph.
// So each question now gets its own place, and none of them share words:
//   which side is true?  -> which COLUMN the cell sits in
//   what did I mark?     -> a filled teal BOX ("I marked this"), nothing else
//   what was correct?    -> a green UNDERLINE under the key box, pick-independent
//   was I right?         -> a VERDICT CHIP, in second-person verbs
//                           ("Doğru bildin" / "Yanlış bildin" / "Boş bıraktın")
// The chip's wording deliberately avoids the bare words "doğru"/"yanlış" as labels,
// so it can never be re-read as a claim about the statement.
const TrueFalseActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: ITrueFalseActivityMaterialProps) => {
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  // Statement order: a DETERMINISTIC shuffle keyed on the activity content, so
  // the SAME activity always renders in the SAME order. This order must survive
  // the solve -> graded-result transition: the solve screens swap this grid for
  // a loading spinner while the answer saves, which unmounts and remounts it. A
  // plain Math.random() shuffle would re-seed to a NEW order on that remount,
  // making the rows appear to jump around the moment answers are revealed. The
  // content-keyed seed also never leaks the trueList-then-falseList source order.
  const materialMapKey = JSON.stringify(materialMap);
  const shuffleOptionList = useMemo(
    () =>
      seededShuffleArray(
        [...(materialMap.trueList || []), ...(materialMap.falseList || [])],
        materialMapKey,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [materialMapKey],
  );

  useEffect(() => {
    setDisplayAnswerMap(showCorrectAnswer ? materialMap : answerMap);
  }, [showCorrectAnswer, materialMap, answerMap]);

  const renderStatement = (value: string, key: string) => {
    if (resolveMaterialMediaType(value, contentMap.type) !== "TEXT") {
      return (
        <ShowMaterialMediaByContentType
          key={key}
          contentType={resolveMaterialMediaType(value, contentMap.type)}
          src={value}
          canFullScreen={true}
        />
      );
    }
    return (
      <>
        {constructInputWithSpecialExpressionList(value).map((part, i) => (
          <span
            key={i}
            className={`${part.isBold ? "mc-bold" : ""} ${part.isUnderline ? "mc-underline" : ""}`}
          >
            {part.isEquation ? <InlineMath math={part.value} /> : part.value}
          </span>
        ))}
      </>
    );
  };

  const canAnswer = checkCanAnswerQuestion();
  const graded = isPreview;
  // "Your answer" affordances (the marked box, the stripe, the verdict chip, the
  // legend) apply only when we are showing a learner's OWN answer — not a plain
  // correct-answer reveal (showCorrectAnswer swaps displayAnswerMap for the answer
  // key, so every row would read as "marked & correct").
  const showLearnerMarks = graded && !showCorrectAnswer;
  const hasLearnerChoice =
    (displayAnswerMap.trueList || []).length > 0 ||
    (displayAnswerMap.falseList || []).length > 0;
  // With no learner answer at all the view degrades to a bare key reveal: the key
  // is simply filled green, unambiguous because no learner marks share the screen.
  const gradeAgainstLearner = showLearnerMarks && hasLearnerChoice;
  // The third axis. Present only when there is a learner answer to pass verdict on.
  const showVerdict = gradeAgainstLearner;
  const showLegend = gradeAgainstLearner;

  return (
    <div className={`mc-root mc-tf2${showVerdict ? " is-graded" : ""}`}>
      <div className="mc-tf2-head">
        <span />
        <span className="mc-tf2-col">{i18n.t("true")}</span>
        <span className="mc-tf2-col">{i18n.t("false")}</span>
        {showVerdict ? (
          <span className="mc-tf2-col mc-tf2-col-verdict">
            {i18n.t("activity_result")}
          </span>
        ) : null}
      </div>
      {shuffleOptionList.map((option, index) => {
        const correctSide = (materialMap.trueList || []).includes(option)
          ? "TRUE"
          : "FALSE";
        const chosenTrue = (displayAnswerMap.trueList || []).includes(option);
        const chosenFalse = (displayAnswerMap.falseList || []).includes(option);
        const answered = chosenTrue || chosenFalse;
        const rowCorrect =
          (correctSide === "TRUE" && chosenTrue) ||
          (correctSide === "FALSE" && chosenFalse);

        // The verdict — the third axis. A skipped row gets BLANK rather than
        // falling through to "no verdict", which is what previously let a row the
        // learner never touched read as though it had simply not been graded.
        const verdictKind = !showVerdict
          ? null
          : !answered
            ? "blank"
            : rowCorrect
              ? "correct"
              : "wrong";

        const boxClass = (side: "TRUE" | "FALSE") => {
          const chosen = side === "TRUE" ? chosenTrue : chosenFalse;
          if (graded) {
            let className = "";
            // ONE visual for the key, pick-independent: the green underline is the
            // same whether or not the learner chose it. (The previous encoding drew
            // the correct answer two different ways — solid green when picked, a
            // pale fill when not — so the legend could only ever describe one of
            // them.) On a bare key reveal there are no learner marks to confuse it
            // with, so the key is simply filled instead.
            if (side === correctSide)
              className += gradeAgainstLearner ? " is-key" : " is-correct";
            // A filled box means exactly one thing, everywhere: "I marked this".
            if (chosen && showLearnerMarks) className += " is-marked";
            return className;
          }
          return chosen ? " is-on" : "";
        };
        // No glyph inside a box we are grading AGAINST A LEARNER. A ✓/✗ in a cell
        // whose column is headed DOĞRU/YANLIŞ is the collision this layout exists to
        // remove — there, the verdict is the chip's job.
        // The bare key reveal is the one exception: with no learner marks on screen
        // a ✓ cannot be read as a verdict, so it can only mean "this is the correct
        // side", which AGREES with the column instead of fighting it. That path also
        // renders no chip and no legend, so without the ✓ it would be a bare green
        // box with nothing naming it as the answer.
        const mark = (side: "TRUE" | "FALSE") => {
          const chosen = side === "TRUE" ? chosenTrue : chosenFalse;
          if (graded) {
            if (gradeAgainstLearner) return "";
            return side === correctSide ? "✓" : "";
          }
          return chosen ? "✓" : "";
        };

        return (
          <div key={index} className="mc-tf2-row">
            {verdictKind ? (
              <span
                className={`mc-tf2-stripe is-${verdictKind}`}
                aria-hidden="true"
              />
            ) : null}
            <span className="mc-tf2-text">
              {renderStatement(option, `${uniqueValue}-${index}`)}
            </span>
            <button
              type="button"
              className={`mc-tf2-box${boxClass("TRUE")}`}
              disabled={!canAnswer}
              aria-label={i18n.t("true")}
              onClick={() => onChange(answerMap, "TRUE", option)}
            >
              {mark("TRUE")}
            </button>
            <button
              type="button"
              className={`mc-tf2-box${boxClass("FALSE")}`}
              disabled={!canAnswer}
              aria-label={i18n.t("false")}
              onClick={() => onChange(answerMap, "FALSE", option)}
            >
              {mark("FALSE")}
            </button>
            {verdictKind ? (
              <span className={`mc-tf2-verdict is-${verdictKind}`}>
                <span className="mc-tf2-verdict-ic" aria-hidden="true">
                  {verdictKind === "correct"
                    ? "✓"
                    : verdictKind === "wrong"
                      ? "✗"
                      : "—"}
                </span>
                {i18n.t(`activity_verdict_${verdictKind}`)}
              </span>
            ) : null}
          </div>
        );
      })}
      {/* Both swatches are the LITERAL grid visuals — a filled box and the key
          underline — so the legend cannot describe a symbol the grid never draws,
          and cannot drift from it later. */}
      {showLegend ? (
        <div className="mc-tf2-legend">
          <span className="mc-tf2-legend-item">
            <span className="mc-tf2-legend-box is-marked" />
            {i18n.t("activity_your_mark")}
          </span>
          <span className="mc-tf2-legend-item">
            <span className="mc-tf2-legend-key" />
            {i18n.t("activity_correct_answer")}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default TrueFalseActivityMaterialContent;
